// Video walkthroughs shown on /demos.
//
// `slug` is the permanent, shareable identity of a video and lives at
// /demos/video/<slug>. Share THAT link, never the YouTube URL — if the
// recording is re-cut or re-uploaded, swap `youtubeId` here and every link
// already in the wild keeps working. Slugs are permanent: don't rename one
// after it has been shared.
//
// `paragraphs` shows on both the /demos card and the video page — keep it to a
// short hook. `highlights`, `callout`, `chapters`, `disclaimer` and `footnote`
// are optional and render on the video page only, so a long description doesn't
// blow out the card grid. `chapters[].t` is "m:ss" and doubles as the player
// start offset. Use `disclaimer` for professional-responsibility caveats — it
// renders as a caveat, distinct from the `callout` used for selling points.
//
// Order here is the order they appear on /demos. It runs overview -> setup ->
// daily use, so a first-time visitor meets them in the order they'd watch them,
// not newest-first.
const videos = [
  {
    slug: 'monelillm-private-ai',
    title: 'MoneliLLM: Private AI That Never Leaves Your Machine',
    youtubeId: 'MRZ1kthVcVE',
    duration: '3 min',
    paragraphs: [
      'Most AI tools send your questions and documents to servers on the internet. That means your contracts, customer lists, and trade secrets leave the building every time someone asks a question. MoneliLLM is different: the AI model runs entirely on your own computer, so you get real artificial intelligence while your business data never leaves your machine.',
      'In this 3-minute walkthrough, you\'ll see exactly how MoneliLLM works — from setting up a private workspace, to "training" it on your own confidential documents, to getting trustworthy answers with citations, all powered by a local on-device model. No cloud. No data leaks. No monthly per-seat AI bills.'
    ]
  },
  {
    slug: 'monelillm-setup-walkthrough',
    title: 'Setting Up MoneliLLM: Workspaces, Documents and Team Access',
    youtubeId: '8AMt1tFs5RU',
    duration: '6 min',
    paragraphs: [
      'The setup walkthrough for the person who runs MoneliLLM day to day — no technical background needed. We deliver the machine with the AI model, the indexing and the storage already configured, so this video skips all of that and covers only the parts you decide: what the assistant knows, how strictly it answers, and who is allowed to ask.',
      'Starting from a completely blank install, on a machine with no cloud connection.'
    ],
    highlights: {
      title: "What you'll see",
      items: [
        'Where the settings live — one icon at the bottom of the sidebar',
        'What a workspace is: a private room with its own documents and its own people',
        'Giving a workspace its knowledge by uploading your own documents',
        'Proving it works, with the answer showing which document it came from',
        'The three ways to work with a workspace — Chat, Query and Agent — and when to use each',
        "Creating accounts for your team, all stored on your own machine",
        'The three roles: administrator, manager, and regular user',
        'Assigning people to a workspace, so each team only sees its own room',
        'The built-in help guide your staff can ask, offline'
      ]
    },
    chapters: [
      { t: '0:00', label: 'What you actually have to set up' },
      { t: '0:31', label: 'Where the settings live' },
      { t: '1:00', label: 'A workspace is a private room' },
      { t: '1:30', label: 'Give the workspace its knowledge' },
      { t: '2:11', label: 'Check that it answers' },
      { t: '2:30', label: 'Three ways to work with a workspace' },
      { t: '3:15', label: 'Choosing the mode and saving it' },
      { t: '3:31', label: "Creating your team's accounts" },
      { t: '3:50', label: 'The three roles' },
      { t: '4:21', label: 'Giving people access to a workspace' },
      { t: '5:04', label: 'Help your team help themselves' },
      { t: '5:21', label: 'Ready for your business' }
    ],
    footnote: "MoneliLLM by Moneli Automation — private AI for businesses that can't send their data to the cloud. It runs on a Mac you own: no API keys, no per-question billing, and no internet connection required. We configure the technical side before it reaches you."
  },
  {
    slug: 'monelillm-knowledge-base',
    title: 'Building Your Knowledge Base: From Empty Install to Cited Answers',
    youtubeId: 'B_fy0K8QCaQ',
    duration: '4 min',
    paragraphs: [
      "Most AI tools send your questions and your files to somebody else's servers. MoneliLLM doesn't. The model runs on your own computer, your documents are indexed on that same machine, and nothing leaves the building.",
      'This walkthrough starts with a completely empty install — no workspaces, no documents — and ends with a working knowledge base your team can ask questions of. Everything you see is the real product running on a real machine: a real upload, real embedding, and real answers from a local model.'
    ],
    highlights: {
      title: "What you'll see",
      items: [
        'Creating a workspace for a subject area',
        'Uploading three confidential policy documents from your own computer',
        'Embedding them so the assistant can use them — once per document',
        'Asking one question that is answered from two different files',
        'Opening the sources to read the exact paragraph an answer came from',
        'Pinning a document so it is always considered',
        'Searching the library and retiring documents that are out of date',
        'Where the index is stored, and how to reset it'
      ]
    },
    chapters: [
      { t: '0:00', label: 'An empty assistant' },
      { t: '0:24', label: 'Create a workspace' },
      { t: '0:42', label: 'The document library' },
      { t: '1:06', label: 'Upload your documents' },
      { t: '1:36', label: 'Add them to the workspace' },
      { t: '2:04', label: 'Ask across your documents' },
      { t: '2:29', label: 'Check where it came from' },
      { t: '2:50', label: 'A second question' },
      { t: '3:06', label: 'Pin what matters most' },
      { t: '3:24', label: 'Find and remove documents' },
      { t: '3:42', label: 'What the assistant has learned' },
      { t: '3:55', label: 'Your knowledge base, your machine' }
    ],
    footnote: "MoneliLLM by Moneli Automation — private AI for businesses that can't send their data to the cloud. Runs on a Mac you own, with no subscription per question, no API keys, and no internet connection required."
  },
  {
    slug: 'monelillm-trustworthy-answers',
    title: 'Answers You Can Verify: Sources, Threads and Query Mode',
    youtubeId: '5SZ9ROJJwdI',
    duration: '4 min',
    paragraphs: [
      "An AI answer you can't verify isn't much use in a business. This video shows the everyday habits that make a private AI assistant genuinely trustworthy: asking in plain English, reading the sources behind each answer, and choosing how strictly the assistant must stick to your documents.",
      'We start from an empty install and build a support desk workspace on camera — a service manual and a warranty policy — then work exactly the way your team would.'
    ],
    highlights: {
      title: "What you'll see",
      items: [
        'Asking about a real fault code and getting the steps back from your own manual',
        'Opening Sources to read the exact passage, with match scores',
        'Follow-up questions that keep their context',
        'Copy, regenerate and delete on any answer',
        'Threads, so each customer or case stays separate',
        "Chat mode vs Query mode — and a live refusal when the documents don't cover the question",
        'Tuning the system prompt, creativity and how much history the assistant keeps'
      ]
    },
    callout:
      "That refusal is the important part: instead of inventing a plausible answer, the assistant tells you it doesn't know. That's what makes it safe to put in front of customers.",
    chapters: [
      { t: '0:00', label: 'A workspace for your team' },
      { t: '0:21', label: 'Load the documents people ask about' },
      { t: '0:45', label: 'Ask the way you would ask a colleague' },
      { t: '1:10', label: 'Read the sources' },
      { t: '1:37', label: 'Follow up without repeating yourself' },
      { t: '1:55', label: 'Message actions' },
      { t: '2:15', label: 'Threads keep conversations separate' },
      { t: '2:33', label: 'Chat mode and Query mode' },
      { t: '3:02', label: 'When the answer is not in your documents' }
    ],
    footnote: "MoneliLLM by Moneli Automation — private AI for businesses that can't send their data to the cloud. It runs on a Mac you own: no API keys, no per-question billing, and no internet connection required. We configure the technical side before it reaches you."
  },
  {
    slug: 'monelillm-team-access',
    title: 'Sharing MoneliLLM With Your Team: Roles, Access and Invite Links',
    youtubeId: 'Cax7Qr9mBkE',
    duration: '4 min',
    paragraphs: [
      'A private AI assistant is only useful if the whole team can use it. This walkthrough shows how to share one MoneliLLM install across a team while keeping control of who can see which documents — all from accounts that live on your own machine, with no cloud directory and no external sign-in.',
      'We start from an empty install, build an HR helpdesk workspace with a real onboarding document, prove it answers, and then hand it out.'
    ],
    highlights: {
      title: "What you'll see",
      items: [
        'Creating the workspace and knowledge your team will share',
        'Where accounts live, and adding a manager and a regular user',
        'What each of the three roles can do: admin, manager, default',
        'Per-workspace membership — the finance workspace stays invisible to the warehouse',
        'Invite links, so people set their own password instead of you emailing one',
        'Reviewing what your team asked, and the administrative event log'
      ]
    },
    callout:
      'The whole staff list, every conversation and every document stays on the machine you own.',
    chapters: [
      { t: '0:00', label: 'Start with something worth protecting' },
      { t: '0:33', label: 'It works — now share it' },
      { t: '0:51', label: 'Where accounts live' },
      { t: '1:13', label: 'Add a manager' },
      { t: '1:31', label: 'Add a regular user' },
      { t: '1:50', label: 'What each role can do' },
      { t: '2:16', label: 'Who can open which workspace' },
      { t: '2:50', label: 'Invite people without handing out passwords' },
      { t: '3:09', label: 'See what your team is asking' },
      { t: '3:27', label: 'The event log' },
      { t: '3:42', label: 'Your team, your rules' }
    ]
  },
  {
    slug: 'monelillm-for-hr-teams',
    title: 'Stop Answering the Same HR Questions: Private AI for People Teams',
    youtubeId: 'lrWY8d-GcYo',
    duration: '3 min',
    paragraphs: [
      'How much parental leave do we get? When does the benefits window close? How many days notice for a week off?',
      "Your team already wrote the answers — they're in the handbook, the leave policy and the benefits guide. Nobody reads them, so the questions come to HR instead.",
      'MoneliLLM turns those documents into something your staff can simply ask. It runs on a computer in your own office, so salary bands, disciplinary notes and everything else stays exactly where it should — which means you can load the real internal documents, not a sanitised version.',
      'In this walkthrough we start with a blank install and build a people and policies workspace on camera, then ask the questions HR fields every month.'
    ],
    highlights: {
      title: "What you'll see",
      items: [
        'A private workspace for people and policies',
        'Loading the leave policy, the pay and benefits guide and the onboarding guide',
        'Asking about paid parental leave and getting the detail — including whether it can be split',
        'Opening the source so staff can check the policy themselves',
        'The awkward follow-up: someone missed the benefits enrolment window',
        'Keeping a separate, restricted room for the documents only HR should see'
      ]
    },
    callout:
      'Everything here is the real software running on a real machine. The company is fictional; the workflow is not.',
    chapters: [
      { t: '0:00', label: 'The same twelve questions, every month' },
      { t: '0:36', label: 'One room for people and policies' },
      { t: '0:50', label: 'Your handbook, leave policy and benefits guide' },
      { t: '1:14', label: 'Ask the way your staff would ask' },
      { t: '1:37', label: 'Show them the policy it came from' },
      { t: '1:59', label: 'The awkward follow-up question' },
      { t: '2:19', label: 'What H.R. keeps to itself' },
      { t: '2:39', label: 'Confidential by construction' }
    ],
    footnote: 'MoneliLLM by Moneli Automation — private AI for businesses that cannot send their data to the cloud. Runs on a Mac you own: no API keys, no per-question billing, no internet connection required.'
  },
  {
    slug: 'monelillm-for-law-firms',
    title: 'Private AI for Law Firms — Without Sending Client Files Anywhere',
    youtubeId: 'fxbZMElVPWA',
    duration: '3 min',
    paragraphs: [
      "Every lawyer has had the thought: it would take thirty seconds to ask an AI what the notice period is in this lease. And then the second thought — I can't put a client's file into somebody else's website.",
      "That instinct is right, and this is the version where you don't have to choose. MoneliLLM runs on a computer in your own office. The model, the documents and the index all sit on that machine. Nothing is uploaded, nothing trains anyone else's system, and it works with the internet unplugged.",
      "In this walkthrough we start with a blank install and build a client matters workspace on camera — a commercial lease, the firm's standard engagement letter, and the file handling policy — then ask the questions a paralegal actually asks."
    ],
    highlights: {
      title: "What you'll see",
      items: [
        'A private workspace for client matters, with its own documents and its own member list',
        'Asking for the notice period in a lease and getting the delivery requirements too',
        'Opening the source to read the exact clause the answer relied on',
        'A second question answered from a different document — does the retainer cover appeals?',
        'Per-matter access, so only the people on a file can reach it'
      ]
    },
    callout:
      'Everything in this video is the real software running on a real machine. The documents are fictional; the workflow is not.',
    chapters: [
      { t: '0:00', label: 'What you cannot paste into a public AI' },
      { t: '0:36', label: 'A private room for client matters' },
      { t: '0:57', label: 'The documents your matters live in' },
      { t: '1:23', label: 'Ask what a paralegal would ask' },
      { t: '1:48', label: 'Read the clause it relied on' },
      { t: '2:13', label: 'A second question, a different document' },
      { t: '2:31', label: 'Only the people on the matter' },
      { t: '2:50', label: 'Privilege stays in the office' }
    ],
    disclaimer:
      "A note on what this is not: MoneliLLM does not give legal advice and does not replace a lawyer's judgment. It finds and quotes the passage in your own documents so you can decide.",
    footnote: 'MoneliLLM by Moneli Automation — private AI for firms that cannot send client data to the cloud. Runs on a Mac you own: no API keys, no per-question billing, no internet connection required.'
  }
];

export const videoPath = (video) => `/demos/video/${video.slug}`;

export const findVideoBySlug = (slug) => videos.find((v) => v.slug === slug);

/** "m:ss" (or "h:mm:ss") -> seconds, for the player's start offset. */
export const timeToSeconds = (t) =>
  t.split(':').reduce((total, part) => total * 60 + Number(part), 0);

export default videos;
